import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  MdSend,
  MdImage,
  MdClose,
  MdDelete,
  MdRestoreFromTrash,
} from "react-icons/md";
import ChatServices, { chatImageUrl } from "../../services/ChatServices";
import AuthImage from "../../components/AuthImage";
import { AuthContext } from "../../context/AuthContext";
import "../../styles/supportChat.css";

const MOBILE_ROLE = 4;
const PAGE_SIZE = 30;

/** Clock only - the date lives in the day separator above the run. */
const timeOnly = (value) =>
  value
    ? new Date(value).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const dayKey = (value) => (value ? new Date(value).toDateString() : "");

const dayLabel = (value) => {
  if (!value) return "";
  const d = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const initialOf = (name) => (name || "?").trim().charAt(0).toUpperCase() || "?";

/**
 * Support inbox: conversation list on the left, thread on the right.
 *
 * There is no socket here on purpose - TopBar owns the single connection and
 * re-broadcasts as `yantra:chatMessage`, so this page listens on window and a
 * second tab cannot open a duplicate socket or raise a duplicate toast.
 */
const SupportChat = () => {
  const { user } = useContext(AuthContext);
  const api = useMemo(() => new ChatServices(), []);

  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [filter, setFilter] = useState("");

  const [activeId, setActiveId] = useState(null);
  const [activePlayer, setActivePlayer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // Chosen file plus its local preview URL, cleared together on send/remove.
  const [attachment, setAttachment] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  // Cursor paging: oldest id loaded so far, and whether anything precedes it.
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Message whose attachment is being deleted, held while the choice is made.
  const [deleting, setDeleting] = useState(null);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  // Set just before older messages are prepended; the layout effect uses it to
  // hold the reader's place instead of letting the browser jump.
  const restoreRef = useRef(null);
  // Read inside socket callbacks, which capture their first render's closure.
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const loadConversations = useCallback(
    async (search) => {
      try {
        const res = await api.getConversations(1, 50, search);
        if (res?.isSuccess) setConversations(res.data || []);
      } catch (_err) {
        // The list simply stays as it was; a toast per poll would be noise.
      } finally {
        setLoadingList(false);
      }
    },
    [api],
  );

  useEffect(() => {
    loadConversations(filter);
  }, [loadConversations, filter]);

  const openThread = useCallback(
    async (playerId) => {
      setActiveId(playerId);
      setLoadingThread(true);
      try {
        const res = await api.getMessages(playerId, PAGE_SIZE);
        if (res?.isSuccess) {
          // API is newest-first; display oldest-first.
          setMessages([...(res.data || [])].reverse());
          setActivePlayer(res.player || null);
          setHasMore(!!res.has_more);
        }
        await api.markRead(playerId);
        setConversations((prev) =>
          prev.map((c) =>
            Number(c.user_id) === Number(playerId)
              ? { ...c, unread_count: 0 }
              : c,
          ),
        );
        // TopBar owns the header badge and cannot see this call, so tell it to
        // re-count instead of leaving a stale number for up to 15s.
        window.dispatchEvent(new CustomEvent("yantra:chatRead"));
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Could not open conversation",
        );
      } finally {
        setLoadingThread(false);
      }
    },
    [api],
  );

  // Land on the newest conversation instead of an empty pane. Guarded on
  // activeId so a background refresh - or a socket-driven reorder - can never
  // yank the reader off the thread they are already reading.
  useEffect(() => {
    if (!activeId && conversations.length > 0) {
      openThread(Number(conversations[0].user_id));
    }
  }, [conversations, activeId, openThread]);

  // Live messages arrive via TopBar's socket, re-broadcast on window.
  useEffect(() => {
    const onMessage = (event) => {
      const row = event?.detail;
      if (!row) return;
      const playerId = Number(row.user_id);

      if (playerId === Number(activeIdRef.current)) {
        setMessages((prev) =>
          prev.some((m) => m.id === row.id) ? prev : [...prev, row],
        );
        if (Number(row.sender_role) === MOBILE_ROLE) {
          api.markRead(playerId).catch(() => {});
        }
      }
      // Reorder the list and refresh unread counts either way.
      loadConversations(filter);
    };

    // Someone else removed an attachment: drop it here too, or this thread
    // keeps a thumbnail whose URL now 404s.
    const onImageDeleted = (event) => {
      const id = Number(event?.detail?.id);
      if (!id) return;
      setMessages((prev) =>
        prev.map((m) =>
          Number(m.id) === id ? { ...m, image: null, image_deleted: 1 } : m,
        ),
      );
    };

    window.addEventListener("yantra:chatMessage", onMessage);
    window.addEventListener("yantra:chatImageDeleted", onImageDeleted);
    return () => {
      window.removeEventListener("yantra:chatMessage", onMessage);
      window.removeEventListener("yantra:chatImageDeleted", onImageDeleted);
    };
  }, [api, loadConversations, filter]);

  /**
   * Pull the page before the oldest message on screen.
   *
   * Cursor, not page number: a message arriving while the reader scrolls would
   * shift every offset by one and quietly skip a row.
   */
  const loadOlder = useCallback(async () => {
    if (!activeId || loadingOlder || !hasMore || messages.length === 0) return;

    setLoadingOlder(true);
    try {
      const oldestId = Number(messages[0].id);
      const res = await api.getMessages(activeId, PAGE_SIZE, oldestId);
      if (res?.isSuccess) {
        const older = [...(res.data || [])].reverse();
        if (older.length) {
          const el = scrollRef.current;
          // Remember the height so the restore below can offset by exactly
          // what got added.
          restoreRef.current = el ? el.scrollHeight : null;
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            return [...older.filter((m) => !seen.has(m.id)), ...prev];
          });
        }
        setHasMore(!!res.has_more);
      }
    } catch (_err) {
      // Leave what is already loaded; the reader can scroll up again.
    } finally {
      setLoadingOlder(false);
    }
  }, [api, activeId, hasMore, loadingOlder, messages]);

  /**
   * Either hold the reader's place after a prepend, or pin to the newest.
   *
   * Layout effect so it runs before paint - doing this in useEffect lets the
   * browser show the jumped position for a frame first.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (restoreRef.current != null) {
      // Older messages went in above: keep the same message under the cursor.
      el.scrollTop = el.scrollHeight - restoreRef.current;
      restoreRef.current = null;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Reaching the top asks for the previous page.
  const onScroll = (e) => {
    if (e.currentTarget.scrollTop <= 40) loadOlder();
  };

  const removeImage = async (mode) => {
    const target = deleting;
    if (!target) return;
    setDeleting(null);
    try {
      const res = await api.deleteImage(target.id, mode);
      if (res?.isSuccess) {
        toast.success(res.message);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === target.id ? { ...m, image: null, image_deleted: 1 } : m,
          ),
        );
      } else {
        toast.error(res?.message || "Could not delete image");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not delete image");
    }
  };

  const restoreImage = async (id) => {
    try {
      const res = await api.restoreImage(id);
      if (res?.isSuccess) {
        toast.success(res.message);
        // The filename is not returned, so refetch the thread to get it back.
        if (activeId) openThread(activeId);
      } else {
        toast.error(res?.message || "Could not restore image");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not restore image");
    }
  };

  const pickImage = (file) => {
    if (!file) return;
    if (!/^image\//i.test(file.type || "")) {
      toast.error("Only image files can be attached");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    // Revoke the previous preview before replacing it.
    setAttachment((prev) => {
      if (prev?.preview) URL.revokeObjectURL(prev.preview);
      return { file, preview: URL.createObjectURL(file) };
    });
  };

  const clearAttachment = () => {
    setAttachment((prev) => {
      if (prev?.preview) URL.revokeObjectURL(prev.preview);
      return null;
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    // An image on its own is a complete message.
    if ((!text && !attachment) || !activeId || sending) return;

    setSending(true);
    try {
      const res = await api.sendMessage(activeId, text, attachment?.file);
      if (res?.isSuccess) {
        setDraft("");
        clearAttachment();
        // The box was grown inline as the reply was typed; clearing the value
        // does not shrink it back on its own.
        if (inputRef.current) inputRef.current.style.height = "auto";
        setMessages((prev) =>
          prev.some((m) => m.id === res.data.id) ? prev : [...prev, res.data],
        );
        loadConversations(filter);
      } else {
        toast.error(res?.message || "Could not send message");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="sc-wrap">
      <div className="sc-body">
        <aside className="sc-list">
          <input
            className="sc-search"
            placeholder="Search name, game ID or mobile"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />

          {loadingList ? (
            <div className="sc-empty">Loading…</div>
          ) : conversations.length === 0 ? (
            <div className="sc-empty">No conversations yet</div>
          ) : (
            conversations.map((c) => {
              const id = Number(c.user_id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`sc-item${id === Number(activeId) ? " sc-item-active" : ""}`}
                  onClick={() => openThread(id)}
                >
                  <span className="sc-avatar" aria-hidden="true">
                    {initialOf(c.player_name)}
                  </span>
                  <span className="sc-item-body">
                    <span className="sc-item-name">
                      {c.player_name || "Player"}
                    </span>
                    <span className="sc-item-meta">
                      {c.game_id} · {c.mobile}
                    </span>
                  </span>
                  {c.unread_count > 0 && (
                    <span className="sc-badge">{c.unread_count}</span>
                  )}
                </button>
              );
            })
          )}          
        </aside>

        <section className="sc-thread">
          {!activeId ? (
            <div className="sc-placeholder">
              Select a conversation to read and reply.
            </div>
          ) : (
            <>
              <div className="sc-thread-head">
                <span className="sc-avatar sc-avatar-lg" aria-hidden="true">
                  {initialOf(activePlayer?.name)}
                </span>
                <div className="sc-thread-who">
                  <strong>{activePlayer?.name || "Player"}</strong>
                  <span>
                    {activePlayer?.game_id} · {activePlayer?.mobile}
                  </span>
                </div>
              </div>

              <div className="sc-messages" ref={scrollRef} onScroll={onScroll}>
                {hasMore && !loadingThread && (
                  <div className="sc-older">
                    {loadingOlder ? (
                      <span className="sc-older-text">Loading earlier messages…</span>
                    ) : (
                      <button
                        type="button"
                        className="sc-older-btn"
                        onClick={loadOlder}
                      >
                        Load earlier messages
                      </button>
                    )}
                  </div>
                )}
                {loadingThread ? (
                  <div className="sc-empty">Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="sc-empty">
                    No messages in this conversation
                  </div>
                ) : (
                  messages.map((m, i) => {
                    const fromPlayer = Number(m.sender_role) === MOBILE_ROLE;
                    const prev = messages[i - 1];
                    const newDay =
                      !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
                    // A run from the same person only needs its name once.
                    const grouped =
                      !!prev &&
                      !newDay &&
                      Number(prev.sender_id) === Number(m.sender_id);

                    return (
                      <Fragment key={m.id}>
                        {newDay && (
                          <div className="sc-day">
                            <span>{dayLabel(m.created_at)}</span>
                          </div>
                        )}
                        <div
                          className={`sc-row ${fromPlayer ? "sc-row-in" : "sc-row-out"}${
                            grouped ? " sc-row-grouped" : ""
                          }`}
                        >
                          <div
                            className={`sc-bubble${m.image ? " sc-bubble-img" : ""}`}
                          >
                            {!fromPlayer && !grouped && (
                              <div className="sc-sender">
                                {m.sender_name || "Staff"}
                              </div>
                            )}
                            {m.image && (
                              <div className="sc-img-wrap">
                                <AuthImage
                                  src={chatImageUrl(m.image)}
                                  alt="Attachment"
                                  className="sc-img"
                                  onClick={() =>
                                    setLightbox(chatImageUrl(m.image))
                                  }
                                />
                                <button
                                  type="button"
                                  className="sc-img-del"
                                  title="Delete image"
                                  aria-label="Delete image"
                                  onClick={() => setDeleting(m)}
                                >
                                  <MdDelete size={15} />
                                </button>
                              </div>
                            )}
                            {!m.image && !!m.image_deleted && (
                              <div className="sc-img-gone">
                                <MdImage size={15} />
                                <span>Image deleted</span>
                                <button
                                  type="button"
                                  className="sc-img-restore"
                                  title="Restore if the file still exists"
                                  onClick={() => restoreImage(m.id)}
                                >
                                  <MdRestoreFromTrash size={14} />
                                </button>
                              </div>
                            )}
                            {m.message && (
                              <div className="sc-text">{m.message}</div>
                            )}
                            <div className="sc-time">{timeOnly(m.created_at)}</div>
                          </div>
                        </div>
                      </Fragment>
                    );
                  })
                )}
              </div>

              {attachment && (
                <div className="sc-attach">
                  <img
                    src={attachment.preview}
                    alt="Selected attachment"
                    className="sc-attach-thumb"
                  />
                  <span className="sc-attach-name">
                    {attachment.file.name}
                  </span>
                  <button
                    type="button"
                    className="sc-attach-x"
                    onClick={clearAttachment}
                    aria-label="Remove attachment"
                  >
                    <MdClose size={15} />
                  </button>
                </div>
              )}

              <form className="sc-composer" onSubmit={send}>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => pickImage(e.target.files?.[0])}
                />
                <button
                  type="button"
                  className="sc-attach-btn"
                  onClick={() => fileRef.current?.click()}
                  title="Attach an image"
                  aria-label="Attach an image"
                >
                  <MdImage size={19} />
                </button>
                <textarea
                  ref={inputRef}
                  className="sc-input"
                  rows={1}
                  maxLength={2000}
                  placeholder="Type a reply…"
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    // Grow with the text up to the CSS max-height, then scroll.
                    e.target.style.height = "auto";
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  onKeyDown={(e) => {
                    // Enter sends, Shift+Enter makes a new line.
                    if (e.key === "Enter" && !e.shiftKey) send(e);
                  }}
                />
                <button
                  type="submit"
                  className="sc-send"
                  disabled={(!draft.trim() && !attachment) || sending}
                  title={sending ? "Sending…" : "Send"}
                  aria-label="Send message"
                >
                  <MdSend size={18} />
                </button>
              </form>
            </>
          )}
        </section>
      </div>

      {deleting && (
        <div
          className="sc-confirm-back"
          onClick={() => setDeleting(null)}
          role="presentation"
        >
          <div
            className="sc-confirm"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <h4>Delete this image?</h4>
            <p>
              Hiding keeps the file on the server so it can be brought back.
              Deleting permanently frees the disk space and cannot be undone.
              The message text stays either way.
            </p>
            <div className="sc-confirm-actions">
              <button
                className="sc-confirm-btn sc-confirm-ghost"
                onClick={() => setDeleting(null)}
              >
                Cancel
              </button>
              <button
                className="sc-confirm-btn sc-confirm-soft"
                onClick={() => removeImage("soft")}
              >
                Hide only
              </button>
              <button
                className="sc-confirm-btn sc-confirm-hard"
                onClick={() => removeImage("permanent")}
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div
          className="sc-lightbox"
          onClick={() => setLightbox(null)}
          role="presentation"
        >
          <button className="sc-lightbox-x" aria-label="Close">
            <MdClose size={22} />
          </button>
          <AuthImage src={lightbox} alt="Attachment" className="sc-lightbox-img" />
        </div>
      )}
    </div>
  );
};

export default SupportChat;
